import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Search, Instagram, Camera, Video, PhoneCall, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_ICONS = {
  instagram: { label: "Instagram", color: "text-pink-400" },
  tiktok: { label: "TikTok", color: "text-cyan-400" },
  twitter: { label: "Twitter", color: "text-blue-400" },
  onlyfans: { label: "OnlyFans", color: "text-sky-400" },
  fansly: { label: "Fansly", color: "text-purple-400" },
  otro: { label: "Otro", color: "text-gray-400" },
};

export default function CreadoresViewPanel() {
  const [models, setModels] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedModel, setSelectedModel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModels = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/models/chatter-view`, { withCredentials: true });
        setModels(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("[Creadores] Error:", err?.response?.status);
        setModels([]);
      }
      setLoading(false);
    };
    fetchModels();
  }, []);

  const filtered = models.filter(
    (m) =>
      m.status !== "inactiva" &&
      m.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="text-gray-400 text-center py-12">Cargando creadoras...</div>;
  }

  if (selectedModel) {
    return (
      <ModelDetailView model={selectedModel} onBack={() => setSelectedModel(null)} />
    );
  }

  return (
    <div className="space-y-4">
      <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            placeholder="Buscar creadora por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-700 text-white"
            data-testid="creadores-search"
          />
        </div>
      </Card>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No hay creadoras que coincidan.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((m) => (
            <Card
              key={m.id}
              onClick={() => setSelectedModel(m)}
              className="bg-[#0a0a0a] border border-white/[0.08] hover:border-pink-500/40 rounded-2xl p-4 cursor-pointer transition-all"
              data-testid={`creadora-card-${m.id}`}
            >
              <div className="flex items-start gap-3">
                {m.photo_url ? (
                  <img
                    src={m.photo_url}
                    alt={m.name}
                    className="w-20 h-20 rounded-xl object-cover border-2 border-pink-500/30 shrink-0"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-xl bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center text-3xl shrink-0">
                    👤
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-white font-bold text-lg truncate">{m.name}</h3>
                  {m.personalidad && (
                    <p className="text-pink-300 text-xs italic line-clamp-2 mt-1">
                      🎭 {m.personalidad}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(m.tip_menu || []).length > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300">
                        💵 {m.tip_menu.length} tips
                      </span>
                    )}
                    {Object.values(m.social_links || {}).some((v) => v) && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300">
                        🔗 Redes
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ModelDetailView({ model: m, onBack }) {
  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <button
        onClick={onBack}
        className="text-pink-400 hover:text-pink-300 text-sm flex items-center gap-1"
        data-testid="creadora-back-btn"
      >
        ← Volver al listado
      </button>

      {/* Header con foto */}
      <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          {m.photo_url ? (
            <img
              src={m.photo_url}
              alt={m.name}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover border-2 border-pink-500/40 shrink-0"
            />
          ) : (
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-zinc-800 border-2 border-zinc-700 flex items-center justify-center text-5xl shrink-0">
              👤
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="text-white text-2xl font-bold flex items-center gap-2">
              <Crown className="w-5 h-5 text-pink-400" /> {m.name}
            </h2>
            {m.personalidad && (
              <p className="text-pink-300 text-sm italic mt-2">🎭 {m.personalidad}</p>
            )}
            {m.año_ingreso && (
              <p className="text-gray-400 text-xs mt-2">📅 Desde {m.año_ingreso}</p>
            )}
          </div>
        </div>
      </Card>

      {/* Tarifas */}
      {(m.precio_custom_video > 0 || m.precio_custom_photo > 0 || m.precio_videollamada > 0) && (
        <Card className="bg-[#0a0a0a] border border-green-500/20 rounded-2xl p-4">
          <h3 className="text-green-300 font-semibold mb-3 text-sm">💰 Tarifas Estándar</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {m.precio_custom_video > 0 && (
              <div className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-3">
                <Video className="w-6 h-6 text-pink-400" />
                <div>
                  <div className="text-gray-400 text-xs">Custom Video</div>
                  <div className="text-white font-bold">${m.precio_custom_video}</div>
                </div>
              </div>
            )}
            {m.precio_custom_photo > 0 && (
              <div className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-3">
                <Camera className="w-6 h-6 text-rose-400" />
                <div>
                  <div className="text-gray-400 text-xs">Custom Foto</div>
                  <div className="text-white font-bold">${m.precio_custom_photo}</div>
                </div>
              </div>
            )}
            {m.precio_videollamada > 0 && (
              <div className="bg-zinc-800/50 rounded-lg p-3 flex items-center gap-3">
                <PhoneCall className="w-6 h-6 text-purple-400" />
                <div>
                  <div className="text-gray-400 text-xs">Videollamada</div>
                  <div className="text-white font-bold">${m.precio_videollamada}</div>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Tip Menu */}
      {(m.tip_menu || []).length > 0 && (
        <Card className="bg-[#0a0a0a] border border-yellow-500/20 rounded-2xl p-4">
          <h3 className="text-yellow-300 font-semibold mb-3 text-sm">💵 Tip Menu</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {m.tip_menu.map((item, idx) => {
              const min = Number(item.precio) || 0;
              const max = Number(item.precio_max) || 0;
              const showRange = max > min;
              return (
                <div
                  key={idx}
                  className="flex justify-between items-center bg-zinc-800/50 rounded-lg p-3 border border-yellow-500/10"
                >
                  <span className="text-white text-sm">{item.nombre || "—"}</span>
                  <span className="text-yellow-400 font-bold">
                    {showRange ? `$${min} - $${max}` : `$${min}`}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Redes Sociales */}
      {m.social_links && Object.values(m.social_links).some((v) => v) && (
        <Card className="bg-[#0a0a0a] border border-cyan-500/20 rounded-2xl p-4">
          <h3 className="text-cyan-300 font-semibold mb-3 text-sm flex items-center gap-2">
            <Instagram className="w-4 h-4" /> Redes Sociales
          </h3>
          <div className="space-y-2">
            {Object.entries(m.social_links)
              .filter(([_, url]) => url)
              .map(([plat, url]) => {
                const info = SOCIAL_ICONS[plat] || { label: plat, color: "text-gray-400" };
                return (
                  <a
                    key={plat}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 text-sm ${info.color} hover:underline break-all`}
                  >
                    <span className="capitalize font-medium w-24 shrink-0">{info.label}:</span>
                    <span className="truncate">{url}</span>
                  </a>
                );
              })}
          </div>
        </Card>
      )}

      {/* Info operativa */}
      {(m.biografia || m.usuarios_potenciales || m.metodologia_chat || m.expresion_chat) && (
        <Card className="bg-[#0a0a0a] border border-white/[0.08] rounded-2xl p-4 space-y-3">
          {m.biografia && (
            <div>
              <h4 className="text-purple-300 font-semibold text-xs uppercase tracking-wide mb-1">
                Biografía
              </h4>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{m.biografia}</p>
            </div>
          )}
          {m.usuarios_potenciales && (
            <div className="border-t border-white/5 pt-3">
              <h4 className="text-blue-300 font-semibold text-xs uppercase tracking-wide mb-1">
                Usuarios Potenciales
              </h4>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{m.usuarios_potenciales}</p>
            </div>
          )}
          {m.metodologia_chat && (
            <div className="border-t border-white/5 pt-3">
              <h4 className="text-cyan-300 font-semibold text-xs uppercase tracking-wide mb-1">
                Metodología de Chat
              </h4>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{m.metodologia_chat}</p>
            </div>
          )}
          {m.expresion_chat && (
            <div className="border-t border-white/5 pt-3">
              <h4 className="text-rose-300 font-semibold text-xs uppercase tracking-wide mb-1">
                Expresión / Tono
              </h4>
              <p className="text-gray-300 text-sm whitespace-pre-wrap">{m.expresion_chat}</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
